import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { LocalStorageService } from '../../services/local-storage.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import { filter, finalize, interval, Subscription, switchMap, timer } from 'rxjs';
import { TracerService } from '../../services/tracer.service';
import { Request, Session, Trace, Version } from '../../models/tracer.model';
import { ToastrService } from 'ngx-toastr';
import { DecimalPipe, NgClass, NgOptimizedImage } from '@angular/common';
import 'cally';
import { NgxPaginationModule } from 'ngx-pagination';
import { GetConsumerPipe } from '../../pipes/get-consumer-pipe';
import { GetAgencyPipe } from '../../pipes/get-agency-pipe';
import { FormatDatePipe } from '../../pipes/format-date-pipe';
import { GetProvidersPipe } from '../../pipes/get-providers-pipe';
import { CollapseProvidersPipe } from '../../pipes/collapse-providers-pipe';
import { IsProviderCallbackPipe } from '../../pipes/is-provider-callback-pipe';
import { GetStatusPipe } from '../../pipes/get-status-pipe';
import { GetSessionStatusPipe } from '../../pipes/get-session-status-pipe';
import { SessionProvidersPipe } from '../../pipes/session-providers-pipe';
import { SessionLastActionPipe } from '../../pipes/session-last-action-pipe';
import Keycloak from 'keycloak-js';
import { describeApiError } from '../../services/api-error-message';

// How often Live Update refreshes the session list, in ms.
const liveUpdateIntervalMs = 30000;

@Component({
  selector: 'app-home',
  imports: [
    FormsModule,
    NgxSpinnerComponent,
    NgClass,
    NgOptimizedImage,
    DecimalPipe,
    NgxPaginationModule,
    GetConsumerPipe,
    GetAgencyPipe,
    FormatDatePipe,
    GetProvidersPipe,
    CollapseProvidersPipe,
    IsProviderCallbackPipe,
    GetStatusPipe,
    GetSessionStatusPipe,
    SessionProvidersPipe,
    SessionLastActionPipe,
    RouterLink,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home implements OnInit, OnDestroy {
  consumer: string | null = null;
  currentPage = 1;
  // perPage default is 20 (was 10) to leave headroom for client-side
  // filtering: prepareCollapsedRequests() drops whole sessions whose
  // every request is IsCollapsed (background traffic). With 10 from the
  // backend, the visible count after filtering routinely dropped to 5–7
  // — operators wanted "at least 10" on screen. 20 reliably surfaces
  // 10+ post-filter rows in typical traffic without overwhelming the
  // server (DISTINCT-on-(session_id, created_at) cost is sub-linear in
  // perPage at this size).
  perPage = 20;
  // perPageMin clamps the parsed `per_page` query param so a stale URL
  // can't force a tiny page below the threshold the UX expects.
  private static readonly perPageMin = 10;
  searchModel = {
    application: '',
    consumer: '',
    provider: '',
    method: '',
    status: '',
    agent: '',
    agency: '',
    api_version: '',
    // Order references. Server-side exact match against the indexed
    // requests.order_id / requests.pnr columns (tracer migration 80),
    // upper-cased there, so casing here does not matter. Both are
    // request-level filters ANDed with the rest: a session is listed
    // when ONE of its requests satisfies every filter set.
    order_id: '',
    pnr: '',
    date_from: '',
    date_to: '',
    important: false,
    background: false,
    // anonymous → server-side opt-in to include sessions composed entirely
    // of auth-failed (Consumer="unknown") traces. Off by default so the
    // default session list isn't dominated by buggy clients hammering
    // sandbox with bad credentials. See AGWTracer/tracer page_controller.go
    // GetIndex for the matching server-side filter.
    anonymous: false,
  };
  loaded: boolean = false;
  showVersion = {};
  versions: { [key: string]: Version } = {};
  sessions: Session[] = [];
  sessionsCount = 0;
  preparedSessionData: Session[] = [];
  collapsedIdxOpened: number[] = [];
  isSessionVisible: Record<number, boolean> = {};
  isDateFromPopoverOpen = false;
  isDateToPopoverOpen = false;
  enableLiveUpdate = false;
  intervalSubscription?: Subscription;
  // True from the start of a search() until its response settles. Live
  // Update reads this to skip ticks instead of stacking a second call on
  // top of one still in flight.
  private searchInFlight = false;
  allowedMethods: string[] = [
    'AirShopping',
    'OfferPrice',
    'OrderCreate',
    'AirDocIssue',
    'SeatAvailability',
    'ServiceList',
    'OrderChange (AddSeats)',
    'OrderChange (RemoveSeats)',
    'OrderChange (AddServices)',
    'OrderChange (RemoveServices)',
    'OrderChange (AcceptDisruption)',
    'OrderChange (OrderRebook)',
    'OrderChange (OrderSplit)',
    'OrderChange (PayPendingItems)',
    'OrderChange (OrderChangeUnknown)',
    'OrderChange (OrderReserve)',
    'OrderChange',
    'OrderChangeNotif',
    'OrderRetrieve',
    'OrderReshop',
    'OrderReshopReprice',
    'OrderReshopReprice (OrderReprice)',
    'OrderReshopRefund',
    'OrderCancel',
    'OrderUpdate',
    'PaymentInfo',
    'OrderNotificationSeenToggle',
  ];
  filteredMethods: string[] = [...this.allowedMethods];
  showOptions = false;
  selectedRealm: string | null = null;
  highlightedMethodIndex = -1;

  // Stats card state for the unauthenticated landing view. Fetched
  // from the public /stats endpoint in ngOnInit when the user isn't
  // signed in. Null before resolution so the template can show a
  // neutral placeholder.
  stats: {
    sessions: number;
    requests: number;
    sessions_replayed: number;
    requests_replayed: number;
  } | null = null;

  // Login tab state — drives which login button gets the primary
  // styling and which realm gets passed to selectRealm() on submit.
  loginTab: 'agents' | 'admins' = 'agents';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ls: LocalStorageService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private tracerService: TracerService,
    protected keycloak: Keycloak
  ) {}

  ngOnInit() {
    if (!!this.keycloak.authenticated) {
      this.consumer = this.ls.getItem('consumer');
      this.parseURL();
      this.search();
    } else {
      this.selectedRealm = this.ls.getItem('selectedRealm');
      if (this.selectedRealm) {
        this.keycloak.login({ redirectUri: window.location.origin });
        this.ls.removeItem('selectedRealm');
      }
      // Lazy-fetch the public stats for the landing view. Endpoint is
      // server-cached for 60s so refresh-spamming the page is cheap.
      this.tracerService.getStats().subscribe({
        next: stats => (this.stats = stats),
        error: () => (this.stats = null),
      });
    }
  }

  parseURL(): void {
    const q = this.route.snapshot.queryParams;

    if (q['application']) this.searchModel.application = q['application'];
    if (q['consumer']) this.searchModel.consumer = q['consumer'];
    if (q['provider']) this.searchModel.provider = q['provider'];
    if (q['method']) this.searchModel.method = q['method'];
    if (q['status']) this.searchModel.status = q['status'];
    if (q['agent']) this.searchModel.agent = q['agent'];
    if (q['agency']) this.searchModel.agency = q['agency'];
    if (q['api_version']) this.searchModel.api_version = q['api_version'];
    if (q['order_id']) this.searchModel.order_id = q['order_id'];
    if (q['pnr']) this.searchModel.pnr = q['pnr'];
    if (q['date_from']) this.searchModel.date_from = q['date_from'];
    if (q['date_to']) this.searchModel.date_to = q['date_to'];
    if (q['important']) this.searchModel.important = q['important'];
    if (q['background']) this.searchModel.background = q['background'];
    if (q['anonymous']) this.searchModel.anonymous = q['anonymous'] === 'true';
    if (q['current_page']) this.currentPage = parseInt(q['current_page'], 10);
    if (q['per_page']) {
      const parsed = parseInt(q['per_page'], 10);
      this.perPage = Number.isFinite(parsed) ? Math.max(parsed, Home.perPageMin) : this.perPage;
    }
  }

  buildURL(): void {
    const q: any = {};

    const s = this.searchModel;

    if (s.application) q.application = s.application;
    if (s.consumer) q.consumer = s.consumer;
    if (s.provider) q.provider = s.provider;
    if (s.method) q.method = s.method;
    if (s.status) q.status = s.status;
    if (s.agent) q.agent = s.agent;
    if (s.agency) q.agency = s.agency;
    if (s.api_version) q.api_version = s.api_version;
    if (s.order_id) q.order_id = s.order_id.trim();
    if (s.pnr) q.pnr = s.pnr.trim();
    if (s.date_from) q.date_from = s.date_from;
    if (s.date_to) q.date_to = s.date_to;
    if (s.important) q.important = s.important;
    if (s.background) q.background = s.background;
    if (s.anonymous) q.anonymous = s.anonymous;
    if (this.currentPage) q.current_page = this.currentPage;
    if (this.perPage) q.per_page = this.perPage;

    this.router
      .navigate(['/'], {
        queryParams: q,
      })
      .catch(err => {
        // optional error handling
        console.error('Navigation error:', err);
      });
  }

  search() {
    this.buildURL();
    this.loaded = false;
    this.searchInFlight = true;
    this.spinner.show();

    this.showVersion = {};
    this.sessions = [];

    timer(250)
      .pipe(
        switchMap(() => this.tracerService.getTracerData()),
        finalize(() => {
          this.loaded = true;
          this.searchInFlight = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: res => {
          if (!res.error) {
            this.sessions = res.sessions;
            this.sortSessionsByUpdatedAt();

            const currentPage = this.currentPage > 0 ? this.currentPage : 1;

            if (res.sessions) {
              if (res.sessions.length < this.perPage) {
                this.sessionsCount = (currentPage - 1) * this.perPage + res.sessions.length;
              } else {
                this.sessionsCount = currentPage * this.perPage + 1;
              }

              this.prepareCollapsedRequests();
            }
          } else {
            this.preparedSessionData = [];
            const failure = describeApiError(
              { status: 200, error: { error: res.error } },
              'load the sessions list'
            );
            this.toastr.error(failure.detail, failure.title);
          }
        },
        error: error => {
          this.preparedSessionData = [];
          const failure = describeApiError(error, 'load the sessions list');
          this.toastr.error(failure.detail, failure.title);
        },
      });
  }

  prepareCollapsedRequests(): void {
    this.preparedSessionData = [];
    this.collapsedIdxOpened = [];

    let collapsedIdx = 0;
    const filteredSessions: Session[] = [];

    for (const s of this.sessions) {
      const filteredRequests: Request[] = [];

      for (const r of s.requests || []) {
        if (this.searchModel.background || !r.is_collapsed) {
          filteredRequests.push(r);
        }
      }

      if (filteredRequests.length > 0) {
        filteredSessions.push({
          ...s,
          requests: filteredRequests,
        });
      }
    }

    filteredSessions.forEach((session, index) => {
      const preparedRequests: any[] = [];
      let i = 0;
      const n = session.requests.length;

      while (i < n) {
        const current = session.requests[i];

        if (current?.is_collapsed && !this.isErrorInTracer(current.traces || [])) {
          let j = i + 1;
          while (
            j < n &&
            session.requests[j]?.is_collapsed &&
            !this.isErrorInTracer(session.requests[j].traces || [])
          ) {
            j++;
          }

          if (j - i > 1) {
            const collapsedCountInfo: Record<string, number> = {};
            const collapsedRequests: Request[] = [];

            for (let k = i; k < j; k++) {
              const req = session.requests[k];
              if (req) {
                collapsedRequests.push(req);
                collapsedCountInfo[req.method] = (collapsedCountInfo[req.method] || 0) + 1;
              }
            }

            preparedRequests.push({
              isCollapsedArrayRequests: true,
              collapsedIdx: collapsedIdx,
              collapsedCountInfo: this.prepareCollapsedCountInfo(collapsedCountInfo),
              requests: collapsedRequests,
            });

            collapsedIdx++;
            i = j;
            continue;
          }
        }

        preparedRequests.push(current);
        i++;
      }

      this.preparedSessionData.push({
        ...session,
        requests: preparedRequests,
      });

      this.isSessionVisible[index] = false;
    });
  }

  prepareCollapsedCountInfo(collapsedCountInfo: Record<string, number>): string {
    const parts = Object.entries(collapsedCountInfo).map(
      ([method, count]) => `${count} ${method} call${count > 1 ? 's' : ''}`
    );
    return `(${parts.join(', ')})`;
  }

  private sortSessionsByUpdatedAt(): void {
    if (!this.sessions || this.sessions.length === 0) return;

    this.sessions.sort((a, b) => {
      return b.updated_at.localeCompare(a.updated_at);
    });
  }

  isErrorInTracer(traces: Trace[] = []): boolean {
    return traces.some(trace => trace?.status === 'error' || trace?.status === 'http_error');
  }

  reset() {
    this.searchModel = {
      application: '',
      consumer: '',
      provider: '',
      method: '',
      status: '',
      agent: '',
      agency: '',
      api_version: '',
      order_id: '',
      pnr: '',
      date_from: '',
      date_to: '',
      important: false,
      background: false,
      anonymous: false,
    };
  }

  onDateFromPicked(event: Event) {
    const calendar = event.target as any;
    this.searchModel.date_from = calendar.value;
    this.isDateFromPopoverOpen = false;
  }

  onDateToPicked(event: Event) {
    const calendar = event.target as any;
    this.searchModel.date_to = calendar.value;
    this.isDateToPopoverOpen = false;
  }

  // Live Update refreshes the session list on a timer.
  //
  // Ticks are dropped while a refresh is still in flight. Previously
  // every tick started an independent search() with no guard and no
  // cancellation, so when /index was slow the browser kept stacking
  // more copies of the most expensive query in the system on top of
  // each other — during the 2026-08-17 prod incident that endpoint was
  // taking minutes, i.e. up to ~24 concurrent copies per open tab.
  // Skipping rather than cancelling is deliberate: a refresh that is
  // nearly done should be allowed to land.
  //
  // The cadence is 30s, not 10s. Ten seconds only ever made sense on
  // the assumption the call returns quickly, and the list is a
  // monitoring view, not a live tail.
  toggleLiveUpdate() {
    if (this.enableLiveUpdate) {
      this.intervalSubscription?.unsubscribe();
      this.intervalSubscription = undefined;
    } else {
      this.intervalSubscription = interval(liveUpdateIntervalMs)
        .pipe(filter(() => !this.searchInFlight))
        .subscribe(() => {
          this.search();
        });
    }
    this.enableLiveUpdate = !this.enableLiveUpdate;
  }

  // Without this the Live Update timer outlives the component: leaving
  // the page and coming back left the previous interval running and
  // still calling search(), one more leaked poller per visit.
  ngOnDestroy(): void {
    this.intervalSubscription?.unsubscribe();
    this.intervalSubscription = undefined;
  }

  onPageChange(newPage: number): void {
    this.currentPage = newPage;
    this.search();
  }

  filterMethods() {
    this.highlightedMethodIndex = -1;
    const value = this.searchModel.method.toLowerCase();
    this.filteredMethods = this.allowedMethods.filter(m => m.toLowerCase().includes(value));
  }

  selectOption(method: string) {
    this.searchModel.method = method;
    this.showOptions = false;
  }

  selectTypedValue() {
    this.showOptions = false;
  }

  hideOptions() {
    setTimeout(() => (this.showOptions = false), 150);
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.showOptions || this.filteredMethods.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedMethodIndex =
          (this.highlightedMethodIndex + 1) % this.filteredMethods.length;
        this.scrollHighlightedIntoView();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightedMethodIndex =
          (this.highlightedMethodIndex - 1 + this.filteredMethods.length) %
          this.filteredMethods.length;
        this.scrollHighlightedIntoView();
        break;

      case 'Enter':
        if (this.highlightedMethodIndex >= 0) {
          event.preventDefault();
          this.selectOption(this.filteredMethods[this.highlightedMethodIndex]);
        }
        break;
    }
  }

  scrollHighlightedIntoView() {
    setTimeout(() => {
      const el = document.querySelector('.method-item.active');
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  selectRealm(realm: string) {
    this.ls.setItem('selectedRealm', realm);
    this.ls.setItem('agRealm', realm);
    window.location.reload();
  }
}
